import { useEffect, useState } from 'react';
import { Alert, Button, Input } from 'antd';
import { CheckOutlined, SendOutlined } from '@ant-design/icons';
import { sendOtp, verifyOtp } from '@/api/dataroom';
import { normalizeError } from '@/api/errors';
import type { OtpSent, OtpSpec, Task, TaskStatus } from '@/api/types';
import { useI18n } from '@/i18n/useI18n';
import { useTaskText } from './taskText';

/** Server refusal codes (`OTP_<reason>`) the room has copy for; anything else is the generic line. */
const KNOWN = new Set([
  'OTP_CONTACT_INVALID',
  'OTP_SEND_LIMIT',
  'OTP_RESEND_TOO_SOON',
  'OTP_NO_ACTIVE_CODE',
  'OTP_CODE_WRONG',
  'OTP_CODE_EXPIRED',
  'OTP_CODE_LOCKED',
]);

/**
 * OTP task (OL-22 slice 2): the applicant enters a mobile number or email (per the spec's channel),
 * receives a six-digit code, and enters it. The server owns the code and its limits (10-minute
 * life, 5 tries, resend throttle); the room only asks and relays. On a demo hub the provider is a
 * mock that delivers nothing, so the server returns the code and the room shows it in a banner.
 */
export function OtpTask({ task, onStatus }: { task: Task; onStatus: (status: TaskStatus) => void }) {
  const { t } = useI18n();
  const text = useTaskText(task);
  const channel = (task.spec as OtpSpec | undefined)?.channel ?? 'SMS';
  const [contact, setContact] = useState('');
  const [sent, setSent] = useState<OtpSent | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wait, setWait] = useState(0);

  useEffect(() => {
    if (wait <= 0) return undefined;
    const id = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(id);
  }, [wait]);

  if (task.status === 'ACCEPTED' || done) {
    return <Alert type="success" showIcon message={t('otp.verified')} />;
  }

  function explain(err: unknown) {
    const e = normalizeError(err);
    setError(e.code && KNOWN.has(e.code) ? t(`otp.error.${e.code}`) : t('error.inline'));
    return e;
  }

  async function send() {
    setBusy(true);
    setError(null);
    try {
      const res = await sendOtp(task.taskId, contact.trim());
      setSent(res);
      setCode('');
      setWait(res.resendAfterSeconds);
    } catch (err) {
      explain(err);
    } finally {
      setBusy(false);
    }
  }

  async function verify() {
    setBusy(true);
    setError(null);
    try {
      const res = await verifyOtp(task.taskId, code);
      onStatus(res.status);
      setDone(true);
    } catch (err) {
      const e = explain(err);
      if (e.code === 'OTP_CODE_LOCKED' || e.code === 'OTP_CODE_EXPIRED') setCode('');
    } finally {
      setBusy(false);
    }
  }

  if (!sent) {
    return (
      <div className="dr-stack">
        <label className="dr-field">
          <span>{text('contactLabel', channel === 'SMS' ? 'otp.phoneLabel' : 'otp.emailLabel')}</span>
          <Input
            size="large"
            value={contact}
            type={channel === 'SMS' ? 'tel' : 'email'}
            inputMode={channel === 'SMS' ? 'tel' : 'email'}
            autoComplete={channel === 'SMS' ? 'tel' : 'email'}
            placeholder={text('contactPlaceholder', channel === 'SMS' ? 'otp.phonePlaceholder' : 'otp.emailPlaceholder')}
            onChange={(e) => setContact(e.target.value)}
            data-testid="otp-contact"
          />
        </label>
        {error && <Alert type="error" showIcon message={error} />}
        <Button
          type="primary"
          size="large"
          block
          disabled={!contact.trim()}
          loading={busy}
          icon={<SendOutlined />}
          onClick={send}
        >
          {text('send', 'otp.send')}
        </Button>
      </div>
    );
  }

  return (
    <div className="dr-stack">
      <div className="dr-meta">{t('otp.sentTo', { contact: sent.maskedContact })}</div>
      {sent.demoCode && (
        <Alert
          type="info"
          showIcon
          message={t('otp.demoCode', { code: sent.demoCode })}
          data-testid="otp-demo-code"
        />
      )}
      <label className="dr-field">
        <span>{text('codeLabel', 'otp.codeLabel')}</span>
        <Input
          size="large"
          className="dr-otp__code"
          value={code}
          maxLength={6}
          inputMode="numeric"
          autoComplete="one-time-code"
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          data-testid="otp-code"
        />
      </label>
      {error && <Alert type="error" showIcon message={error} />}
      <Button
        type="primary"
        size="large"
        block
        disabled={code.length !== 6}
        loading={busy}
        icon={<CheckOutlined />}
        onClick={verify}
      >
        {text('submit', 'otp.verify')}
      </Button>
      <div className="dr-otp__actions">
        <Button type="link" disabled={busy || wait > 0 || sent.sendsLeft <= 0} onClick={send}>
          {wait > 0 ? t('otp.resendIn', { seconds: wait }) : t('otp.resend')}
        </Button>
        <Button
          type="link"
          disabled={busy}
          onClick={() => {
            setSent(null);
            setError(null);
          }}
        >
          {t(channel === 'SMS' ? 'otp.changePhone' : 'otp.changeEmail')}
        </Button>
      </div>
    </div>
  );
}
